/**
 * Contexte de la campagne — quelle saison on reconduit, et depuis laquelle.
 *
 * Les cinq écrans de la campagne travaillent tous sur le même couple de
 * saisons (N et N-1). Plutôt que de le repasser en paramètre partout, le
 * layout le résout une fois (URL → mémoire du poste → saison la plus probable)
 * et le publie ici.
 *
 * La saison retenue vit dans l’URL (`?saison=…`) : un lien envoyé à un collègue
 * ouvre exactement le même écran, et le bouton « précédent » du navigateur fait
 * ce qu’on attend de lui.
 */

import { createContext, use } from 'react'

import type { Saison } from '@tb/shared'

export interface ValeurCampagne {
  /** Saison N — celle que l’on ouvre à l’abonnement. `null` : aucune choisie. */
  readonly saisonId: string | null
  /** La saison N complète, quand le catalogue est lisible par cet utilisateur. */
  readonly saison: Saison | null
  /** Saison N-1 — celle dont on reconduit les abonnés (CAM-03). */
  readonly saisonN1Id: string | null
  readonly saisonN1: Saison | null

  readonly saisons: readonly Saison[]
  /**
   * `false` quand `GET /saisons` répond 403 : le compte est un guichet, il
   * travaille sur la saison qu’on lui a transmise mais ne peut pas en changer.
   */
  readonly saisonsLisibles: boolean
  readonly chargementSaisons: boolean

  readonly definirSaison: (saisonId: string) => void
  readonly definirSaisonN1: (saisonN1Id: string) => void
}

export const ContexteCampagne = createContext<ValeurCampagne | null>(null)

/** À n’appeler que sous `<LayoutCampagne>` — sinon c’est un bug de routage. */
export function useCampagne(): ValeurCampagne {
  const valeur = use(ContexteCampagne)
  if (valeur === null) {
    throw new Error('useCampagne doit être utilisé à l’intérieur de <LayoutCampagne>.')
  }
  return valeur
}
